// file: src/services/userServices.js

import request from './requestServices';
import {
  SESSION_STATUS_ENDPOINT,
  FIREBASE_AUTH_ENDPOINT,
  USER_LOGOUT_URL,
  USER_INFO_URL,
} from './apiRoutes';

const userService = {
  /**
   * Check whether we are logged in or not, without forcing a 401 error.
   * Endpoint returns data like: { isLoggedIn: true/false, user?: {...} }
   */
  async sessionStatus() {
    try {
      const { data } = await request(SESSION_STATUS_ENDPOINT, {
        method: 'GET',
      });
      
      // Our API wraps everything in a 'data' field
      // data => { RetCode: 0, data: { isLoggedIn: true/false, user?: {...} } }
      
      // Log the structure of what we're getting back
      console.debug('[Auth] Session status response:', data);
      
      // Return the actual data payload
      return data.data || { isLoggedIn: false };
    } catch (err) {
      console.error('[Auth] Session check failed:', err);
      return { isLoggedIn: false };
    }
  },

  async firebaseAuth({ firebaseToken, oauthProvider, userInfo }) {
    try {
      const { data } = await request(FIREBASE_AUTH_ENDPOINT, {
        method: 'POST',
        data: { 
          firebase_token: firebaseToken, 
          oauth_provider: oauthProvider,
          user_info: userInfo
        },
      });
      // Return both retCode and is_first_time for onboarding flow
      return {
        retCode: data.RetCode,
        isFirstTime: data.data?.is_first_time || false
      };
    } catch (err) {
      // If the request service threw an error with a retCode, return it
      if (err.retCode !== undefined) {
        return { retCode: err.retCode, isFirstTime: false };
      }
      // For other errors (network, etc.), re-throw
      throw err;
    }
  },

  /**
   * Get current user's profile information including teams and tenant details
   * Returns profile data object
   */
  async getProfile() {
    try {
      console.log('[Profile] Fetching user profile...');
      const response = await request('/v1/user/profile', {
        method: 'GET',
      });
      console.log('[Profile] Profile response:', response);
      
      // Handle the actual backend response structure
      if (response && response.data) {
        console.log('[Profile] Extracted data from response:', response.data);
        return response.data;
      } else {
        console.log('[Profile] No data field found, returning response as-is:', response);
        return response;
      }
    } catch (err) {
      console.error('[Profile] Failed to get user profile:', err);
      throw err;
    }
  },

  /**
   * Share a project with a user or team
   * Returns success status and message
   */
  async shareProject({ projectId, targetType, targetId, permission }) {
    try {
      const { data } = await request('/v1/user/project/share', {
        method: 'POST',
        data: {
          project_id: projectId,
          target_type: targetType, // 'user' or 'team'
          target_id: targetId,
          permission // 'read', 'write', 'comment', 'admin'
        },
      });
      return data;
    } catch (err) {
      console.error('[Project] Failed to share project:', err);
      throw err;
    }
  },

  /**
   * Get all shares for a specific project
   * Returns shares data object
   */
  async getProjectShares(projectId) {
    try {
      const { data } = await request(`/v1/user/project/shares/${projectId}`, {
        method: 'GET',
      });
      return data;
    } catch (err) {
      console.error('[Project] Failed to get project shares:', err);
      throw err;
    }
  },

  /**
   * Remove a project share
   * Returns success status and message
   */
  async unshareProject({ projectId, targetType, targetId }) {
    try {
      const { data } = await request('/v1/user/project/unshare', {
        method: 'POST',
        data: {
          project_id: projectId,
          target_type: targetType, // 'user' or 'team'
          target_id: targetId
        },
      });
      return data;
    } catch (err) {
      console.error('[Project] Failed to unshare project:', err);
      throw err;
    }
  },

  /**
   * Get all projects the current user can access
   * Returns accessible projects array
   */
  async getAccessibleProjects() {
    try {
      const { data } = await request('/v1/user/projects/accessible', {
        method: 'GET',
      });
      return data;
    } catch (err) {
      console.error('[Project] Failed to get accessible projects:', err);
      throw err;
    }
  },

  /**
   * Check if a user can share a project with a specific target
   * Returns permission check result
   */
  async checkProjectSharePermission({ projectId, targetType, targetId }) {
    try {
      const { data } = await request('/v1/user/project/share/check', {
        method: 'POST',
        data: {
          project_id: projectId,
          target_type: targetType, // 'user' or 'team'
          target_id: targetId
        },
      });
      return data;
    } catch (err) {
      console.error('[Project] Failed to check share permission:', err);
      throw err;
    }
  },

  /**
   * Protected route: get detailed user info. Will 401 if not logged in.
   */
  async getUserInfo() {
    const { data } = await request(USER_INFO_URL, { method: 'GET' });
    // data => { RetCode:0, data: { ...user info... } }
    return data;
  },

  /**
   * Update user profile information (onboarding data)
   * @param {Object} userInfo - User information to update
   * @param {string} userInfo.name - User's full name
   * @param {string} userInfo.company - Company name
   * @param {string} userInfo.job_title - Job title
   * @param {string} [userInfo.design_problems] - Design problems they're solving
   * @param {string} [userInfo.current_tools] - Current tools they use
   * @param {string} [userInfo.how_heard_about_us] - How they heard about us
   * @param {boolean} [userInfo.consent_to_newsletter] - Newsletter consent
   */
  async updateUserInfo(userInfo) {
    try {
      const { data } = await request('/v1/user/onboarding-info', {
        method: 'POST',
        data: userInfo,
      });
      return data;
    } catch (err) {
      console.error('[User] Failed to update user info:', err);
      throw err;
    }
  },

  /**
   * Log out the current user session.
   */
  async logout() {
    await request(USER_LOGOUT_URL, { method: 'POST' });
  },
};

export default userService;




